import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight, Download, Eye, LoaderCircle, Mail, Phone, ScanSearch, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { downloadCsv } from '../../../helpers/csvExport';
import { CallAPIPostPromise } from '../../../Common/commaon';

type DocumentType = 'identity' | 'insurance' | 'w9' | 'license';
type NormalizedStatus = 'verified' | 'review' | 'missing' | 'rejected' | 'expired';

interface ComplianceDocument {
  document_type?: string;
  verification_status?: string;
  ai_status?: string;
  expiration_date?: string | null;
  updated_at?: string | null;
  issues?: unknown;
}

interface ContractorDocumentStatusListProps {
  contractors: any[];
  loading?: boolean;
  onViewContractor: (contractor: any) => void;
  onRefresh: () => void;
}

const DOCUMENTS: Array<{ type: DocumentType; label: string }> = [
  { type: 'identity', label: 'Identity' },
  { type: 'insurance', label: 'Insurance' },
  { type: 'w9', label: 'W-9' },
  { type: 'license', label: 'License' },
];

const parseJson = (value: unknown): any => {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const getComplianceDocuments = (contractor: any): ComplianceDocument[] => {
  const parsed = parseJson(contractor?.compliance_documents);
  return Array.isArray(parsed) ? parsed : [];
};

const hasLegacyDocument = (contractor: any, type: DocumentType) => {
  if (type === 'insurance') return Boolean(contractor?.cc_insurance_documentation_url);
  if (type === 'w9') return Boolean(contractor?.cc_licence_documentation_url);
  if (type === 'license') return Boolean(contractor?.cc_contractor_documentation_url);
  return false;
};

const normalizeStatus = (contractor: any, type: DocumentType): NormalizedStatus => {
  const document = getComplianceDocuments(contractor).find(
    (item) => String(item?.document_type || '').toLowerCase() === type
  );
  const status = String(document?.verification_status || document?.ai_status || '').toLowerCase();

  if (status === 'verified') return 'verified';
  if (status === 'rejected') return 'rejected';
  if (status === 'expired') return 'expired';
  if (document || hasLegacyDocument(contractor, type)) return 'review';
  return 'missing';
};

const getDocumentRecord = (contractor: any, type: DocumentType) =>
  getComplianceDocuments(contractor).find(
    (item) => String(item?.document_type || '').toLowerCase() === type
  );

const getIssueText = (contractor: any, type: DocumentType) => {
  const issues = parseJson(getDocumentRecord(contractor, type)?.issues);
  return Array.isArray(issues) ? issues.filter(Boolean).join(' ') : '';
};

const getSummary = (contractor: any) => {
  const statuses = DOCUMENTS.map(({ type }) => normalizeStatus(contractor, type));
  return {
    verified: statuses.filter((status) => status === 'verified').length,
    missing: statuses.filter((status) => status === 'missing').length,
    attention: statuses.filter((status) => status !== 'verified').length,
  };
};

const STATUS_META: Record<NormalizedStatus, { label: string; className: string }> = {
  verified: { label: 'Verified', className: 'status-success' },
  review: { label: 'Review needed', className: 'status-pending' },
  missing: { label: 'Missing', className: 'status-missing' },
  rejected: { label: 'Rejected', className: 'status-danger' },
  expired: { label: 'Expired', className: 'status-expired' },
};

const PAGE_SIZE = 25;

export default function ContractorDocumentStatusList({
  contractors,
  loading = false,
  onViewContractor,
  onRefresh,
}: ContractorDocumentStatusListProps) {
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'attention' | 'review' | 'missing' | 'verified'>('attention');
  const [page, setPage] = useState(1);
  const [scanningDocument, setScanningDocument] = useState<string | null>(null);

  const rows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return contractors.filter((contractor) => {
      const summary = getSummary(contractor);
      const haystack = [
        contractor?.user_first_name,
        contractor?.user_last_name,
        contractor?.company_name,
        contractor?.user_email,
        contractor?.user_phone,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'attention' && summary.attention > 0) ||
        (statusFilter === 'review' && DOCUMENTS.some(({ type }) => normalizeStatus(contractor, type) === 'review')) ||
        (statusFilter === 'missing' && summary.missing > 0) ||
        (statusFilter === 'verified' && summary.verified === DOCUMENTS.length);
      return matchesQuery && matchesStatus;
    });
  }, [contractors, query, statusFilter]);

  const totals = useMemo(
    () => ({
      complete: contractors.filter((contractor) => getSummary(contractor).verified === DOCUMENTS.length).length,
      attention: contractors.filter((contractor) => getSummary(contractor).attention > 0).length,
      missing: contractors.reduce((total, contractor) => total + getSummary(contractor).missing, 0),
    }),
    [contractors]
  );

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const visibleRows = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [page, rows]
  );

  useEffect(() => {
    setPage(1);
  }, [query, statusFilter]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, pageCount));
  }, [pageCount]);

  const exportRows = () => {
    downloadCsv('contractor-document-status.csv', rows, [
      { header: 'Contractor', value: (contractor: any) => [contractor.user_first_name, contractor.user_last_name].filter(Boolean).join(' ') },
      { header: 'Company', value: (contractor: any) => contractor.company_name || '' },
      { header: 'Email', value: (contractor: any) => contractor.user_email || '' },
      { header: 'Phone', value: (contractor: any) => contractor.user_phone || '' },
      ...DOCUMENTS.map(({ type, label }) => ({
        header: label,
        value: (contractor: any) => STATUS_META[normalizeStatus(contractor, type)].label,
      })),
      { header: 'Missing Documents', value: (contractor: any) => getSummary(contractor).missing },
    ]);
  };

  const scanDocument = async (contractor: any, type: DocumentType) => {
    const scanKey = `${contractor.id}-${type}`;
    setScanningDocument(scanKey);
    try {
      await CallAPIPostPromise('/admin/compliance/rescan', {
        contractorId: contractor.id,
        documentType: type,
      });
      toast.success('AI scan completed and the document status was updated.');
      onRefresh();
    } catch (error: any) {
      toast.error(error?.message || 'AI document scan failed.');
    } finally {
      setScanningDocument(null);
    }
  };

  return (
    <section className="contractor-compliance-list" aria-labelledby="contractor-document-status-heading">
      <div className="contractor-compliance-summary">
        <div>
          <span><AlertTriangle size={16} /></span>
          <small>Need attention</small>
          <strong>{totals.attention}</strong>
        </div>
        <div>
          <span><CheckCircle2 size={16} /></span>
          <small>Fully verified</small>
          <strong>{totals.complete}</strong>
        </div>
        <div>
          <span><ShieldCheck size={16} /></span>
          <small>Missing documents</small>
          <strong>{totals.missing}</strong>
        </div>
      </div>

      <div className="contractor-compliance-card">
        <div className="contractor-compliance-toolbar">
          <div>
            <h2 id="contractor-document-status-heading">Profile document status</h2>
            <p>Review verification gaps and contact contractors who need to update their profile.</p>
          </div>
          <Button variant="outline" size="sm" onClick={exportRows} disabled={!rows.length}>
            <Download size={15} /> Export CSV
          </Button>
        </div>

        <div className="contractor-compliance-filters">
          <div className="contractor-compliance-search">
            <Search size={16} aria-hidden="true" />
            <Input
              className="contractor-compliance-search-input"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search name, company, email or phone"
              aria-label="Search contractor document status"
            />
          </div>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)} aria-label="Filter document status">
            <option value="attention">Needs attention</option>
            <option value="review">Review needed</option>
            <option value="missing">Missing documents</option>
            <option value="verified">Fully verified</option>
            <option value="all">All contractors</option>
          </select>
          <span className="contractor-compliance-result-count">{rows.length} contractors</span>
        </div>

        <div className="contractor-compliance-table-wrap">
          <table className="contractor-compliance-table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Contact</th>
                {DOCUMENTS.map((document) => <th key={document.type}>{document.label}</th>)}
                <th>Summary</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {!loading && visibleRows.map((contractor) => {
                const summary = getSummary(contractor);
                const name = [contractor.user_first_name, contractor.user_last_name].filter(Boolean).join(' ') || 'Unnamed contractor';
                return (
                  <tr key={contractor.id}>
                    <td>
                      <strong>{name}</strong>
                      <span>{contractor.company_name || 'No company name'}</span>
                    </td>
                    <td>
                      <a href={contractor.user_email ? `mailto:${contractor.user_email}` : undefined}>{contractor.user_email || 'No email'}</a>
                      <a href={contractor.user_phone ? `tel:${contractor.user_phone}` : undefined}>{contractor.user_phone || 'No phone'}</a>
                    </td>
                    {DOCUMENTS.map(({ type }) => {
                      const status = normalizeStatus(contractor, type);
                      const scanKey = `${contractor.id}-${type}`;
                      const isScanning = scanningDocument === scanKey;
                      const document = getDocumentRecord(contractor, type);
                      const isBackgroundScanRunning =
                        String(document?.ai_status || '').toLowerCase() === 'pending';
                      const issue = getIssueText(contractor, type);
                      return (
                        <td key={type} title={issue || undefined}>
                          <div className="contractor-document-status-cell">
                            <Badge variant="outline" className={`document-status-badge ${STATUS_META[status].className}`}>
                              {STATUS_META[status].label}
                            </Badge>
                            {status === 'review' && (
                              <button
                                type="button"
                                className="contractor-ai-scan-button"
                                onClick={() => scanDocument(contractor, type)}
                                disabled={isScanning || isBackgroundScanRunning}
                                aria-label={`Scan ${type} document with AI`}
                                title="Run AI verification"
                              >
                                {isScanning || isBackgroundScanRunning
                                  ? <LoaderCircle size={12} className="is-spinning" />
                                  : <ScanSearch size={12} />}
                                {isScanning || isBackgroundScanRunning ? 'Scanning' : 'AI scan'}
                              </button>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td>
                      <span className={summary.attention ? 'contractor-compliance-attention' : 'contractor-compliance-complete'}>
                        {summary.attention ? `${summary.attention} need attention` : 'Complete'}
                      </span>
                    </td>
                    <td>
                      <div className="contractor-compliance-actions">
                        {contractor.user_email && <a href={`mailto:${contractor.user_email}`} aria-label={`Email ${name}`} title="Email contractor"><Mail size={15} /></a>}
                        {contractor.user_phone && <a href={`tel:${contractor.user_phone}`} aria-label={`Call ${name}`} title="Call contractor"><Phone size={15} /></a>}
                        <button type="button" onClick={() => onViewContractor(contractor)} aria-label={`View ${name} profile`} title="View profile"><Eye size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {loading && (
                <tr><td colSpan={8} className="contractor-compliance-empty">Loading document status…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="contractor-compliance-empty">No contractors match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 && (
          <div className="contractor-compliance-pagination">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}
            </span>
            <div>
              <Button variant="outline" size="sm" onClick={() => setPage((value) => value - 1)} disabled={page === 1}>
                <ChevronLeft size={15} /> Previous
              </Button>
              <span>Page {page} of {pageCount}</span>
              <Button variant="outline" size="sm" onClick={() => setPage((value) => value + 1)} disabled={page === pageCount}>
                Next <ChevronRight size={15} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
